import { Sparkles, ThumbsDown, ThumbsUp } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatarDataHora } from '@/lib/datas'
import { usePermissao } from '@/lib/permissoes'
import {
  useGerarInsight,
  useInsight,
  useVotarNoItem,
  type Insight,
  type ItemDoInsight,
  type TipoDeInsight,
} from './api'

/**
 * O cartão de insight — a única superfície por onde texto de IA chega ao gestor.
 *
 * ===========================================================================
 * TRÊS COISAS SÃO OBRIGATÓRIAS AQUI, E NENHUMA É OPCIONAL POR PROPRIEDADE
 * ===========================================================================
 *
 * 1. A MARCAÇÃO DE IA. O selo "Gerado por IA" é parte do cabeçalho, não uma
 *    variante que uma tela possa desligar. Um texto que interpreta a margem da
 *    frota e não diz de onde veio é indistinguível de um relatório apurado — e
 *    a diferença importa quando ele erra.
 *
 * 2. A FONTE DOS NÚMEROS. Cada item mostra os valores que o sustentam, com o
 *    caminho de onde vieram. Não é enfeite de auditoria: é o que permite ao
 *    gestor conferir a frase contra a tabela ao lado em cinco segundos. E os
 *    números exibidos já passaram pela conferência de citações da API — o que
 *    não conferiu não chegou até aqui.
 *
 * 3. O VOTO. Útil / não útil por ITEM, porque a pergunta que a avaliação faz é
 *    "qual recomendação era ruim", não "o briefing prestou".
 *
 * ===========================================================================
 * O ESTADO INDISPONÍVEL É NORMAL, NÃO É ERRO
 * ===========================================================================
 * Camada desligada, teto do dia estourado, API fora do ar, nenhum insight
 * gerado ainda — os quatro chegam como `disponivel: false` com motivo, e todos
 * são renderizados com a mesma calma. Nada de ícone vermelho: não há nada
 * quebrado, e os números da tela não dependem disto.
 */

interface Props {
  readonly tipo: TipoDeInsight
  readonly titulo: string
  /** Uma frase dizendo o que este insight responde. */
  readonly descricao: string
  /** `false` esconde o botão de gerar (ex.: no painel inicial). */
  readonly permiteGerar?: boolean
}

export function CartaoDeInsight({
  tipo,
  titulo,
  descricao,
  permiteGerar = true,
}: Props): React.ReactElement | null {
  const podeLer = usePermissao('ai_insight.read')
  const podeGerar = usePermissao('ai_insight.create')
  const consulta = useInsight(tipo, podeLer)

  // Sem permissão de leitura o cartão não existe — não é um cartão vazio nem
  // um "sem acesso". Quem não tem a permissão não deve nem saber que a seção
  // existe naquela tela.
  if (!podeLer) return null

  return (
    <Card>
      <CardHeader className="gap-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            {titulo}
            <SeloDeIa />
          </CardTitle>
          {permiteGerar && podeGerar ? <BotaoDeGerar tipo={tipo} /> : null}
        </div>
        <p className="text-sm text-fg-secondary">{descricao}</p>
      </CardHeader>

      <CardContent className="space-y-4">
        {consulta.isPending ? <Carregando /> : null}
        {consulta.isError ? (
          <Indisponivel motivo="Não foi possível carregar o insight agora." />
        ) : null}
        {consulta.data === undefined ? null : <Corpo tipo={tipo} insight={consulta.data} />}
      </CardContent>
    </Card>
  )
}

function SeloDeIa(): React.ReactElement {
  return (
    <Badge variant="info" className="gap-1">
      <Sparkles aria-hidden className="size-3" />
      Gerado por IA
    </Badge>
  )
}

function Carregando(): React.ReactElement {
  return (
    <div className="space-y-3" aria-busy="true" aria-live="polite">
      <span className="sr-only">Carregando o insight…</span>
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-5/6" />
    </div>
  )
}

function Indisponivel({ motivo }: { readonly motivo: string }): React.ReactElement {
  return (
    <div className="rounded-lg border border-border bg-elevated px-4 py-6 text-center">
      <p className="text-sm font-medium text-foreground">Insight indisponível</p>
      <p className="mt-1 text-sm text-fg-secondary">{motivo}</p>
      <p className="mt-3 text-xs text-fg-muted">
        Os números desta tela não dependem da IA — eles seguem sendo calculados normalmente.
      </p>
    </div>
  )
}

function Corpo({
  tipo,
  insight,
}: {
  readonly tipo: TipoDeInsight
  readonly insight: Insight
}): React.ReactElement {
  if (!insight.disponivel || insight.itens.length === 0) {
    return <Indisponivel motivo={insight.motivo ?? 'Ainda não há insight para este período.'} />
  }

  return (
    <>
      <ul className="space-y-4">
        {insight.itens.map((item, indice) => (
          <li key={`${item.titulo}-${String(indice)}`}>
            <ItemDoCartao tipo={tipo} item={item} indice={indice} insightId={insight.insightId} />
          </li>
        ))}
      </ul>

      {insight.rodape === null ? null : (
        <p className="border-t border-border pt-3 text-sm text-fg-secondary">
          <span className="font-medium text-foreground">Ressalva: </span>
          {insight.rodape}
        </p>
      )}

      <Rodape insight={insight} />
    </>
  )
}

function ItemDoCartao({
  tipo,
  item,
  indice,
  insightId,
}: {
  readonly tipo: TipoDeInsight
  readonly item: ItemDoInsight
  readonly indice: number
  readonly insightId: string | null
}): React.ReactElement {
  return (
    <article className="rounded-lg border border-border bg-elevated p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="font-semibold text-foreground">{item.titulo}</h4>
        <div className="flex flex-wrap gap-1">
          <SeloDeUrgencia item={item} />
          <SeloDeAcao item={item} />
        </div>
      </div>

      <p className="mt-2 text-sm leading-relaxed text-fg-secondary">{item.texto}</p>

      <Fontes item={item} />

      {insightId === null ? null : <Voto tipo={tipo} insightId={insightId} indice={indice} />}
    </article>
  )
}

/**
 * As fontes — os números que sustentam a frase.
 *
 * O caminho aparece junto do valor de propósito. Ele é feio e é a informação
 * que importa: `veiculos[2].margem = -184300` diz exatamente qual célula da
 * tabela ao lado o gestor deve olhar para conferir. Um "fonte: relatório de
 * frota" seria bonito e inútil.
 */
function Fontes({ item }: { readonly item: ItemDoInsight }): React.ReactElement | null {
  if (item.fontes.length === 0) return null

  return (
    <dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-2">
      {item.fontes.map((fonte) => (
        <div key={fonte.caminho} className="flex items-baseline gap-1 text-xs">
          <dt className="font-mono text-fg-muted">{fonte.caminho}</dt>
          <dd className="font-semibold tabular-nums text-foreground">{fonte.valor}</dd>
        </div>
      ))}
    </dl>
  )
}

function SeloDeUrgencia({ item }: { readonly item: ItemDoInsight }): React.ReactElement | null {
  if (item.urgencia !== undefined) {
    const variante = item.urgencia === 'ALTA' ? 'warning' : 'neutral'
    return <Badge variant={variante}>{rotuloDeUrgencia(item.urgencia)}</Badge>
  }
  if (item.forca !== undefined) {
    return <Badge variant="neutral">Evidência {item.forca.toLowerCase()}</Badge>
  }
  if (item.confianca !== undefined) {
    return <Badge variant="neutral">Confiança {item.confianca.toLowerCase()}</Badge>
  }
  return null
}

function SeloDeAcao({ item }: { readonly item: ItemDoInsight }): React.ReactElement | null {
  if (item.acao !== undefined) {
    // Nunca só cor: o rótulo carrega o significado, como manda o CLAUDE.md.
    const variante =
      item.acao === 'AUMENTAR' ? 'success' : item.acao === 'REDUZIR' ? 'danger' : 'neutral'
    return (
      <Badge variant={variante}>
        {item.acao === 'AUMENTAR' ? 'Aumentar' : item.acao === 'REDUZIR' ? 'Reduzir' : 'Manter'}
        {item.categoria === undefined ? '' : ` · ${item.categoria}`}
      </Badge>
    )
  }

  if (item.semana !== undefined && item.multiplicadorEmPontosBase !== undefined) {
    return (
      <Badge variant="brand">
        {item.semana.rotulo} · {multiplicadorLegivel(item.multiplicadorEmPontosBase)}
      </Badge>
    )
  }
  return null
}

function Voto({
  tipo,
  insightId,
  indice,
}: {
  readonly tipo: TipoDeInsight
  readonly insightId: string
  readonly indice: number
}): React.ReactElement {
  const votar = useVotarNoItem(tipo)
  const [escolhido, setEscolhido] = useState<boolean | null>(null)

  const registrar = (util: boolean): void => {
    setEscolhido(util)
    votar.mutate({ insightId, itemIndice: indice, util })
  }

  return (
    <div className="mt-3 flex items-center gap-2">
      <span className="text-xs text-fg-muted">Este item ajudou?</span>
      <Button
        type="button"
        size="sm"
        variant={escolhido === true ? 'secondary' : 'ghost'}
        aria-pressed={escolhido === true}
        aria-label="Marcar este item como útil"
        onClick={() => {
          registrar(true)
        }}
      >
        <ThumbsUp aria-hidden className="size-3.5" />
        Útil
      </Button>
      <Button
        type="button"
        size="sm"
        variant={escolhido === false ? 'secondary' : 'ghost'}
        aria-pressed={escolhido === false}
        aria-label="Marcar este item como não útil"
        onClick={() => {
          registrar(false)
        }}
      >
        <ThumbsDown aria-hidden className="size-3.5" />
        Não
      </Button>
      {escolhido === null ? null : (
        <span className="text-xs text-fg-muted" role="status">
          Obrigado.
        </span>
      )}
    </div>
  )
}

/**
 * O rodapé de procedência.
 *
 * `itensDescartados` fica VISÍVEL, e essa é a decisão menos óbvia da tela. É
 * tentador escondê-lo — parece sujeira. Mas ele é o único sinal de que a
 * conferência de citações está trabalhando: um número que sobe de zero para
 * três diz que o modelo passou a inventar, e sem ele na tela essa degradação
 * apareceria apenas como "o briefing anda mais curto".
 */
function Rodape({ insight }: { readonly insight: Insight }): React.ReactElement {
  return (
    <p className="border-t border-border pt-3 text-xs text-fg-muted">
      {insight.geradoEm === null ? null : `Gerado em ${formatarDataHora(insight.geradoEm)}`}
      {insight.modelo === null ? null : ` · ${insight.modelo}`}
      {insight.itensDescartados > 0
        ? ` · ${String(insight.itensDescartados)} item(ns) descartado(s) por citarem número que não confere`
        : null}
    </p>
  )
}

function BotaoDeGerar({ tipo }: { readonly tipo: TipoDeInsight }): React.ReactElement {
  const gerar = useGerarInsight(tipo)
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={gerar.isPending}
      onClick={() => {
        gerar.mutate()
      }}
    >
      {gerar.isPending ? 'Gerando…' : 'Gerar agora'}
    </Button>
  )
}

function rotuloDeUrgencia(urgencia: 'ALTA' | 'MEDIA' | 'BAIXA'): string {
  if (urgencia === 'ALTA') return 'Urgente'
  return urgencia === 'MEDIA' ? 'Atenção' : 'Informativo'
}

/** `13500` → `1,35×`. Pontos-base entram, texto legível sai. */
export function multiplicadorLegivel(pontosBase: number): string {
  return `${(pontosBase / 10_000).toFixed(2).replace('.', ',')}×`
}
